import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, UNSAFE_NavigationContext } from "react-router-dom";

interface UseNavigationGuardOptions {
	when: boolean;
	onConfirm?: () => void;
}


const isSafari = (): boolean => {
	return typeof navigator !== 'undefined' && 
		/^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

export const useNavigationGuard = ({ when, onConfirm }: UseNavigationGuardOptions) => {
	const isSafariBrowser = useRef(isSafari());
	const navigate = useNavigate();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const isActiveRef = useRef(false);
	const hasPushedStateRef = useRef(false);
	const pendingTxRef = useRef<any>(null);
	const routerContext = useContext(UNSAFE_NavigationContext) as any;
    const isHandlingBackRef = useRef(false);

	const pushHistoryState = useCallback(() => {
		try {
			if (!hasPushedStateRef.current) {
				window.history.pushState({ __guard__: true }, document.title, window.location.href);
				hasPushedStateRef.current = true;
			}
		} catch {}
	}, []);

	const handlePopState = useCallback((event: PopStateEvent) => {
		if (!isActiveRef.current) return;
		if (isHandlingBackRef.current) return;
		if (isSafariBrowser.current) {
			isHandlingBackRef.current = true;
			setIsDialogOpen(true);
			setTimeout(() => {
				isHandlingBackRef.current = false;
			}, 0);
			return;
		}
		
		isHandlingBackRef.current = true;
		try { window.history.go(1); } catch {}
		setTimeout(() => {
			setIsDialogOpen(true);
			isHandlingBackRef.current = false;
		}, 0);
	}, []);

	const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
		if (!isActiveRef.current) return;
		e.preventDefault();
		e.returnValue = "";
		return "";
	}, []);

	useEffect(() => {
		isActiveRef.current = when;
		if (when) {
			pushHistoryState();
			window.addEventListener("popstate", handlePopState);
			window.addEventListener("beforeunload", handleBeforeUnload);
			let unblock: (() => void) | null = null;
			try {
				const navigator = routerContext?.navigator;
				if (navigator && typeof navigator.block === "function") {
					unblock = navigator.block((tx: any) => {
						if (!isActiveRef.current) {
							tx.retry();
							return;
						}
						pendingTxRef.current = tx;
						setIsDialogOpen(true);
					});
				}
			} catch {}
			return () => {
				window.removeEventListener("popstate", handlePopState);
				window.removeEventListener("beforeunload", handleBeforeUnload);
				hasPushedStateRef.current = false;
				if (unblock) {
					try { unblock(); } catch {}
				}
			};
		} else {
			setIsDialogOpen(false);
		}
	}, [when, handlePopState, handleBeforeUnload, pushHistoryState, routerContext]);

	const cancelNavigation = useCallback(() => {
		setIsDialogOpen(false);
	}, []);

	const confirmNavigation = useCallback(() => {
		setIsDialogOpen(false);
		isActiveRef.current = false;
		onConfirm && onConfirm();
		const tx = pendingTxRef.current;
		if (tx) {
			pendingTxRef.current = null;
			try { tx.retry(); return; } catch {}
		}
		
		// Safari requires different handling for navigation
		if (isSafariBrowser.current) {
			// For Safari, we need to go back twice in some cases
			// First, try normal navigation
			try { 
				window.history.back(); 
				// Additionally pop another state if we previously added one
				if (hasPushedStateRef.current) {
					setTimeout(() => {
						window.history.back();
					}, 0);
					hasPushedStateRef.current = false;
				}
			} catch { 
				navigate(-1); 
			}
		} else {
			// Standard handling for other browsers
			try { window.history.back(); } catch { navigate(-1); }
		}
	}, [navigate, onConfirm]);

	return {
		isDialogOpen,
		confirmNavigation,
		cancelNavigation,
	};
};

export default useNavigationGuard;


